import { useMemo } from 'react';
import { TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { GetSegmentsRequest, useAdminGetSegments } from '../../../segments/hooks/admin-get-segments.hook';
import { DeliveryPointSegment } from '../../types/delivery-point.type';

type Props = {
  id: string;
  value: DeliveryPointSegment | null;
  attachedSegment?: DeliveryPointSegment;
  required?: boolean;
  onChange: (segment: DeliveryPointSegment | null) => void;
};

const ACTIVE_SEGMENTS_REQUEST: GetSegmentsRequest = { page: 1, limit: 100, filters: { isActive: true } };

const buildSegmentOptions = (
  segments: DeliveryPointSegment[],
  attachedSegment?: DeliveryPointSegment
): DeliveryPointSegment[] => {
  const options = segments.map(({ id, name }) => ({ id, name }));
  const isAttachedSegmentMissing =
    attachedSegment !== undefined && !options.some((option) => option.id === attachedSegment.id);
  const mergedOptions = attachedSegment && isAttachedSegmentMissing ? [...options, attachedSegment] : options;

  return mergedOptions.sort((first, second) => first.name.localeCompare(second.name, 'fr'));
};

export function DeliveryPointSegmentAutocomplete({ id, value, attachedSegment, required, onChange }: Props) {
  const { segments } = useAdminGetSegments(ACTIVE_SEGMENTS_REQUEST);
  const options = useMemo(() => buildSegmentOptions(segments, attachedSegment), [segments, attachedSegment]);

  return (
    <Autocomplete
      id={id}
      size="small"
      value={value}
      options={options}
      groupBy={(option) => option.name.charAt(0).toUpperCase()}
      getOptionLabel={(option) => option.name}
      getOptionSelected={(option, selected) => option.id === selected.id}
      onChange={(event, option: DeliveryPointSegment | null) => onChange(option)}
      renderInput={(params) => (
        <TextField {...params} size="small" variant="outlined" label="Segment" required={required} />
      )}
    />
  );
}
